/* ═══════════════════════════════════════════════════════
   Crew Card rules (client copy)
   ═══════════════════════════════════════════════════════
   Pure functions shared by the admin form (CrewCardSection), the crew Files
   tab, the dashboard badge and the calendar sync. The server keeps a
   line-for-line twin in functions/src/services/crewCard.ts — the
   completeness rule, the snapshot allowlist and the fingerprint MUST stay
   identical on both sides (the twin test pins a shared vector).

   Design: docs/crew-cards/DESIGN.md. Nothing here writes to Firestore.
*/

import { fmtTime, normalizeCrew, migrateJob } from "./utils.js";

export const CREW_ROLES_DEFAULT = [
  "Head Rig operator", "Big Rig operator", "Handheld scanner", "Photos",
  "Wrangling", "Client contact", "Delivery", "Data processing + delivery", "Assisting",
  "LiDAR operator", "Photogrammetry", "Other",
];

export const CREW_CARD_ORIGIN = "https://nycsuite.io";
// Written only by the server (deploy, revoke, opened stamp, delivery refresh;
// deployInFlight is the deploy that has reserved its link and may still be
// sending). The client strips these from every save so a stale tab can never
// roll them back or clear them. Twin: SERVER_OWNED_CARD_FIELDS in functions
// crewCard.ts (the twin test compares the two lists).
export const SERVER_OWNED_CARD_FIELDS = ["lastDeploy", "recipientStatus", "deployLock", "deployInFlight"];
export const stripServerOwned = (card) => {
  const out = { ...card };
  SERVER_OWNED_CARD_FIELDS.forEach(f => { delete out[f]; });
  return out;
};
export const CREW_CARD_EXPIRY_DAYS = 3;
export const STUDIO_ADDRESS_LINE = "62 Flushing Ave, Brooklyn, NY 11205";
export const STUDIO_MAPS_GOOGLE = "https://maps.app.goo.gl/vvesLB7iVuinAt8p6";
export const OFFSITE_CONTACT_NAME = "Ivin Ballen";
export const cardCollection = (kind) => (kind === "client" ? "clientCards" : "crewCards");
/* Provenance of seeded text (build spec item 2): blank cards and seeds write
   it, so a later reseed can tell untouched seeded text from edited text.
   Nothing reads it yet; it is not in the snapshot. Twin in crewCard.ts. */
export const SEED_VERSION = 2;
/* Roles that work away from the session by default: an assignment with one
   of these and no explicit onSite is off site (presenceOf). Twin in crewCard.ts. */
export const OFFSITE_ROLES = ["Data processing + delivery", "Delivery"];
/* Which session types get which sheet (item 6): a Call Sheet on capture,
   setup and strike days, a Session Sheet on capture days only, nothing on a
   flight day. A session without a sessionType counts as capture (hydrated
   single-day sessions are). Twin in crewCard.ts. */
export const SHEET_SESSION_TYPES = { crew: ["capture", "setup", "strike"], client: ["capture"] };

/* ── small helpers (the server's twins: String(v ?? "").trim(), arrays minus falsy entries) ── */
const clean = (v) => String(v ?? "").trim();
const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
const sameName = (a, b) => clean(a).toLowerCase() === clean(b).toLowerCase();
const isUnset = (v) => v === null || v === undefined;

/* ── validation (both twins, clean() first) ──
   A time is HH:mm, 00-23 / 00-59, or it counts as "". style is "cool" or
   else "normcore". lengthMinutes is an integer 1..1440 (a number, or the
   digits a number input hands over) or else unset, read as 0. A scope row's
   gear is a list of cleaned strings, empties dropped, order kept; a v1 row
   carried one string, which reads as [gear] ("" as []). extraGear is one
   line per entry; a textarea's raw string splits on newlines. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const isValidTime = (t) => TIME_RE.test(clean(t));
export const timeOrEmpty = (t) => (isValidTime(t) ? clean(t) : "");
export const styleOf = (v) => (clean(v) === "cool" ? "cool" : "normcore");
export const lengthMinutesOf = (v) => {
  const n = typeof v === "number" ? v : (typeof v === "string" && /^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN);
  return Number.isInteger(n) && n >= 1 && n <= 1440 ? n : 0;
};
export const normalizeGear = (gear) => {
  if (Array.isArray(gear)) return list(gear).map(clean).filter(Boolean);
  const one = clean(gear);
  return one ? [one] : [];
};
const linesOf = (v) => (Array.isArray(v) ? v : clean(v).split("\n")).map(clean).filter(Boolean);
/* The assignment of one crew member: by name, trimmed and case-insensitive
   (one rule for the role gap, the snapshot's crew row, the call and the
   presence). {} when there is none. */
const assignmentFor = (card, name) => list(card?.assignments).find(x => x && typeof x === "object" && sameName(x.crewName, name)) || {};
/* The editor's row for one crew member: the index of the assignment the
   twins would read for the name (the same trimmed, case-insensitive match as
   assignmentFor), or -1. Editor-only: the Suite form updates that row in
   place instead of appending a second one for " john cushman " next to
   "John Cushman", which the sheet would never read. */
export const assignmentIndexOf = (assignments, name) =>
  // The raw array, not list(): the index must address the stored row, holes included.
  (Array.isArray(assignments) ? assignments : []).findIndex(x => x && typeof x === "object" && sameName(x.crewName, name));

/* ── ids ── */
export const crewCardId = (jobDocId, sessionId) => `${jobDocId}__${sessionId}`;

/* The sessions a card can key on: the job's sessions as migrateJob leaves
   them, the hydration the Suite applies to every job it loads (a single-day
   job with a capture date gets one session, id sess_hydrated_<job id>, built
   from its root fields). A job already loaded comes back unchanged, so this
   only matters for one handed over straight from storage. The server twin,
   cardSessionsOf in functions crewCard.ts, ports the same rule; the twin test
   holds it to this one. Before 2026-09-22 the server keyed such a job on
   "root" while the Suite keyed it on the hydrated id, and the session id is
   in the fingerprint: a fresh Reviewed tick read "stale" on the server. */
export const cardSessionsOf = (job) => (job && typeof job === "object" ? list(migrateJob(job).sessions) : []);

/* The session a card keys on. Single-day jobs carry one hydrated session
   (sessions[0]); a job with no session at all (no capture date, or a
   multi-day job with none yet) falls back to "root", and the snapshot then
   reads the root schedule/location fields. */
export const cardSessionId = (job) => clean(cardSessionsOf(job)[0]?.id) || "root";

// Twin of sessionForCard in functions crewCard.ts: ids compared trimmed,
// empty entries skipped, the root fallback's fields trimmed.
export const sessionForCard = (job, sessionId) => {
  const s = cardSessionsOf(job);
  const want = clean(sessionId);
  const found = s.find(x => clean(x?.id) === want);
  if (found) return found;
  if (want === "root" || s.length === 0) {
    return {
      id: "root", sessionType: "capture", date: clean(job?.captureDate),
      callTime: clean(job?.callTime), wrapTime: clean(job?.wrapTime),
      location: clean(job?.location), locationVenue: clean(job?.locationVenue),
      locationAddress: clean(job?.locationAddress), locationCity: clean(job?.locationCity),
      locationState: clean(job?.locationState), locationZip: clean(job?.locationZip),
      isStudio: !!job?.isStudio,
    };
  }
  return null;
};

/* Null = default rule: a card field that carries a default holds null for
   "never chosen", "" for "the admin chose none", a value otherwise. */
export const blankCrewCard = (jobDocId, sessionId) => ({
  id: crewCardId(jobDocId, sessionId),
  jobId: jobDocId,
  sessionId,
  talentInitials: "",
  onSiteWithTalent: "",
  anonymizeClient: null, // null = the default rule (isConfidential: a person client is anonymised); true/false = the admin's choice
  subject: null, // null = never seeded; "" = "Not sure"; else one of CAPTURE_SUBJECTS (validated, never detected, by both twins)
  style: "normcore", // "normcore" | "cool"; outside the fingerprint
  crewCall: "", // "HH:mm" or ""; the crew's call when it differs from the session (talent) call
  clientContact: null, // null = the coordinator; "" = none; else a display string typed by the admin
  scope: [], // [{k, n, line, note, gear: string[]}]
  assignments: [], // [{crewName, role, note, callTime: "HH:mm" | "", onSite: boolean | null}]; null = by role (presenceOf)
  extraGear: [], // free text lines
  checklist: [],
  openItems: [],
  seedVersion: SEED_VERSION,
  lastDeploy: null,
  recipientStatus: [],
});

/* ── small helpers ── */
// Crew names exactly as the server sees them: trimmed, empties dropped. Any
// divergence here changes the fingerprint and the card reads "changed" forever.
export const crewNamesOf = (job) => normalizeCrew(job?.crew).map(clean).filter(Boolean);
// Stable keys for editable list items (React keys; never part of the snapshot).
export const itemKey = () => "k" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export const humanizeTurnaround = (turnaround, unit, tbd) => {
  if (tbd) return "TBD";
  const t = clean(turnaround);
  if (!t) return "";
  // The unit is cleaned BEFORE it is compared, as on the server: " days "
  // reads "days" and " businessDays " reads "business days" on both sides (a
  // difference makes a Reviewed tick made here instantly stale there).
  const u0 = clean(unit);
  const u = u0 === "businessDays" ? "business days" : u0 === "weeks" ? "weeks" : u0 === "days" ? "days" : (u0 || "business days");
  return `${t.replace(/-/g, " to ")} ${u}`;
};

/* How the files go out, read the way the Delivery tab reads it: the
   deliveryMethods list when it has entries, else the legacy single
   deliveryMethod. The sheet used to read only the legacy field, so a job set
   to SFTP on the Delivery tab still printed box.com. Twin: deliveryOf in
   functions crewCard.ts. */
export const deliveryOf = (job) => {
  const methods = list(job?.deliveryMethods).map(clean).filter(Boolean);
  return methods.length ? methods.join(", ") : clean(job?.deliveryMethod);
};

/* ── what is being captured ──
   Both sheets used to assume every session was a person at the studio. The
   client sheet seeded wardrobe notes and "you stand still while the cameras
   fire" onto object and on-location jobs — atlpow_260909, an industrial
   switchgear scan in a Staten Island electrical room, shipped that line to the
   client. Two facts fix it: WHERE the session is (already on the session as
   isStudio) and WHAT is in front of the camera, read here from the quote's
   catalog ids first, then the equipment, then the words. The admin can
   override the answer on the card. When nothing is certain the answer is ""
   and every subject-specific line stays unwritten: an empty section vanishes
   from the sheet, a wrong one costs a redeploy. */
export const CAPTURE_SUBJECTS = ["people", "objects", "places"];
export const CAPTURE_SUBJECT_LABELS = { people: "A person", objects: "Objects", places: "A place" };

// Catalog ids that only ever describe a person in front of the rig or scanner.
const PERSON_CATALOG_IDS = new Set([
  "head_scan", "body_scan", "leo_body", "leo_head", "leo_hands", "leo_torso", "leo_other",
  "lowpoly_body", "lowpoly_session",
]);
const subjectOfCatalogId = (id) => {
  const s = clean(id).toLowerCase();
  if (PERSON_CATALOG_IDS.has(s)) return "people";
  if (s.startsWith("obj_")) return "objects";
  // env_* plus the two estimator ids, which are never in RATE_CATALOG.
  if (s.startsWith("env_") || s === "lidar_estimate" || s === "drone_estimate") return "places";
  return "";
};
const subjectOfEquipment = (equipment) => {
  const e = list(equipment).map(x => clean(x).toLowerCase());
  if (e.some(x => x.includes("head rig") || x.includes("big rig"))) return "people";
  if (e.some(x => x.includes("rtc") || x.includes("portal") || x.includes("mavic") || x.includes("drone"))) return "places";
  return "";
};
const subjectOfWords = (text) => {
  const t = clean(text).toLowerCase();
  // Deliberately narrow. "model" is not here: "processed 3D model" appears on
  // nearly every quote we write and would call every job a person job.
  if (/\b(actor|actress|talent|performer|cast member|person|people|portrait|head scan|body scan|full body|face scan)\b/.test(t)) return "people";
  if (/\b(room|space|site|building|interior|exterior|floor|facility|apartment|stage|lobby|warehouse|environment)\b/.test(t)) return "places";
  if (/\b(object|piece|pieces|prop|props|garment|shoe|shoes|sculpture|product|artifact|assembly|unit)\b/.test(t)) return "objects";
  return "";
};
/* People beat a place, a place beats an object. The more a session involves a
   human standing in front of us, the more the sheet has to say to them. */
const SUBJECT_ORDER = ["people", "places", "objects"];
const strongerSubject = (a, b) => (SUBJECT_ORDER.indexOf(a) <= SUBJECT_ORDER.indexOf(b) ? a : b);

export function detectCaptureSubject({ job, quote }) {
  const lines = list(quote?.lineItems).length ? list(quote.lineItems) : list(job?.lineItems);
  const byId = lines.map(li => subjectOfCatalogId(li?.catalogId)).filter(Boolean);
  if (byId.length) return byId.reduce(strongerSubject);
  const equipment = list(job?.selectedEquipment).length ? list(job.selectedEquipment) : list(job?.equipment);
  return subjectOfEquipment(equipment)
    || subjectOfWords([clean(quote?.summary), clean(job?.description), lines.map(li => clean(li?.description)).join(" ")].join(" "))
    || "";
}
/* A subject the admin has actually settled: one of the three, or "" for
   "Not sure". null/undefined means the card was never seeded. The distinction
   matters: an explicit "" must NOT fall back to detection, or "Not sure"
   snaps straight back to the guess. */
export const isChosenSubject = (v) => typeof v === "string" && (v === "" || CAPTURE_SUBJECTS.includes(v));
/* The subject the sheets use: the card's settled value, else the detection.
   The admin form persists whichever it used, so the server (which never
   detects) fingerprints the same value the form did. */
export const captureSubjectOf = ({ job, quote, card }) => (isChosenSubject(card?.subject) ? card.subject : detectCaptureSubject({ job, quote }));
/* The subject as a snapshot carries it: one of the three, else "" (unknown).
   Both twins validate; neither detects here. */
const subjectOf = (v) => (CAPTURE_SUBJECTS.includes(clean(v)) ? clean(v) : "");

export const rosterEmailFor = (roster, name) => {
  const n = clean(name).toLowerCase();
  const hit = list(roster).find(r => clean(r?.name).toLowerCase() === n);
  return clean(hit?.email).toLowerCase();
};
export const rosterEntryFor = (roster, name) => {
  const n = clean(name).toLowerCase();
  return list(roster).find(r => clean(r?.name).toLowerCase() === n) || null;
};
export const fmtPhone = (p) => {
  const d = clean(p).replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) return `${d.slice(1, 4)}-${d.slice(4, 7)}-${d.slice(7)}`;
  return clean(p);
};

/* ── presence (item 3) ──
   Whether a crew member is at the session: the assignment's onSite when it
   is a boolean, else on site unless the role is one of OFFSITE_ROLES. No
   assignment at all reads on site. The crew snapshot's crew[].onSite, the
   session sheet's second-contact label and the point-person gap all read
   this. Twin: presenceOf in functions crewCard.ts. */
export const presenceOf = (crewCard, name) => {
  const a = assignmentFor(crewCard, name);
  if (typeof a.onSite === "boolean") return a.onSite;
  return !OFFSITE_ROLES.includes(clean(a.role));
};

/* ── anonymise by default (owner's answer 4, item 10) ──
   A person client: the job's client name is non-empty and is, case-
   insensitively, the job's contact name or the name of a linked contact. New
   cards carry anonymizeClient null and take this default; an explicit
   true/false (every card made before 2026-09-22 has one) wins. Twins in
   functions crewCard.ts. */
export const clientIsPerson = (job, contacts) => {
  const client = clean(job?.clientName).toLowerCase();
  if (!client) return false;
  if (client === clean(job?.contactName).toLowerCase()) return true;
  // By id only: a contact without an id is never in the map and a link
  // without a contactId never looks one up, so an id-less record whose name
  // happens to be the client's cannot make the job a person client.
  const contactsById = new Map(list(contacts).filter(c => clean(c?.id)).map(c => [clean(c.id), c]));
  return list(job?.linkedContacts).some(l => {
    const id = clean(l?.contactId);
    return !!id && client === clean(contactsById.get(id)?.name).toLowerCase();
  });
};
export const isConfidential = ({ card, job, contacts }) =>
  card?.anonymizeClient === true || (card?.anonymizeClient !== false && clientIsPerson(job, contacts));

/* ── session eligibility (item 6) ── */
export const isSheetSessionEligible = (kind, session) => {
  const type = clean(session?.sessionType) || "capture";
  return SHEET_SESSION_TYPES[kind === "client" ? "client" : "crew"].includes(type);
};

/* ── crew call precedence (item 12, owner's answer 1) ──
   A crew member's call: their assignment's callTime, else the card's
   crewCall, else the session's callTime, each accepted only as HH:mm. The
   snapshot's crew row carries the first as `call`, the sheet the second as
   `crewCall`, and the session call stays `callTime`. */
export const crewCallFor = ({ card, session, name }) =>
  timeOrEmpty(assignmentFor(card, name).callTime) || timeOrEmpty(card?.crewCall) || timeOrEmpty(session?.callTime);

/* The call sheet status row to show for a crew member: the NEWEST row for
   their current roster address (recipients are addresses on the server; an
   address changed A -> B -> A can leave two rows at A, and the older one is
   history), else their newest row by name (an address since removed from the
   roster). Rows are in the order they were written, oldest first. */
export const crewStatusRowFor = (rows, roster, name) => {
  const newestFirst = [...list(rows)].reverse();
  const email = rosterEmailFor(roster, name);
  return (email && newestFirst.find(s => clean(s?.email).toLowerCase() === email))
    || newestFirst.find(s => clean(s?.name) === clean(name))
    || null;
};

/* ── completeness ──
   Required: date, call time, location, ≥1 crew, ≥1 equipment, prep notes,
   deliverable format, a role per crew member, a roster email per crew member.
   Returns { gaps: [{key,label}], required, filled }. */
export function computeGaps({ job, session, card, roster }) {
  const gaps = [];
  const crew = crewNamesOf(job);
  const equipment = list(job?.selectedEquipment).length ? list(job.selectedEquipment) : list(job?.equipment);
  const s = session || {};
  const hasLocation = !!(s.isStudio || clean(s.locationAddress) || clean(s.location) || clean(s.locationVenue));

  if (!clean(s.date)) gaps.push({ key: "date", label: "Session date" });
  if (!clean(s.callTime)) gaps.push({ key: "callTime", label: "Call time" });
  if (!hasLocation) gaps.push({ key: "location", label: "Location" });
  if (crew.length === 0) gaps.push({ key: "crew", label: "At least one crew member" });
  if (equipment.length === 0) gaps.push({ key: "equipment", label: "At least one piece of equipment" });
  if (!clean(job?.prepNotes)) gaps.push({ key: "prepNotes", label: "Prep notes" });
  if (!clean(job?.deliverableType)) gaps.push({ key: "deliverableType", label: "Deliverable format" });
  // A crew call or a personal call that is not a time. Empty is fine (the
  // next call in the precedence stands); these gaps are extra and leave
  // `required` alone.
  if (clean(card?.crewCall) && !isValidTime(card.crewCall)) gaps.push({ key: "crewCall", label: "Crew call is not HH:mm" });
  crew.forEach(name => {
    const a = assignmentFor(card, name);
    if (!clean(a.role)) gaps.push({ key: `role:${name}`, label: `Assignment for ${name}` });
    if (!rosterEmailFor(roster, name)) gaps.push({ key: `email:${name}`, label: `Roster email for ${name}` });
    if (clean(a.callTime) && !isValidTime(a.callTime)) gaps.push({ key: `time:${name}`, label: `Call time for ${name} is not HH:mm` });
  });
  const required = 7 + crew.length * 2;
  return { gaps, required, filled: Math.max(0, required - gaps.length) };
}

/* ── seeds (admin can edit afterwards) ── */
/* One calibration line per rig on the job (Head Rig and Big Rig each), then
   the standard capture-log line. Prep-note bullets are no longer copied in:
   the prep notes render as their own Notes list under the plan, so they
   would print twice. Existing saved checklists are never rewritten. */
export function seedChecklist({ job, session, quote, subject }) {
  const items = [];
  const equipment = list(job?.selectedEquipment).length ? list(job.selectedEquipment) : list(job?.equipment);
  const rigs = equipment.filter((e, i) => (e === "Head Rig" || e === "Big Rig") && equipment.indexOf(e) === i);
  const call = clean(session?.callTime);
  const subj = isChosenSubject(subject) ? subject : detectCaptureSubject({ job, quote });
  // "Before talent arrival" is a lie on a job with no talent; on those the rig
  // is simply ready by call time.
  const before = subj === "people" || !subj
    ? `talent arrival${call ? " at " + fmtTime(call) : ""}`
    : (call ? `the ${fmtTime(call)} call` : "call");
  rigs.forEach(rig => items.push({ k: itemKey(), text: `${rig} calibrated and test-fired before ${before}.`, source: "auto", enabled: true }));
  items.push({ k: itemKey(), text: "Capture log updated in the Suite: arrival, wrap, issues.", source: "auto", enabled: true });
  return items;
}

const guessGear = (text, equipment) => {
  const t = clean(text).toLowerCase();
  const has = (n) => equipment.includes(n);
  if (/head|face/.test(t) && has("Head Rig")) return "Head Rig";
  if (/body|torso|full/.test(t) && has("Big Rig")) return "Big Rig";
  if (/leo/.test(t) && has("Artec Leo")) return "Artec Leo";
  if (/spider/.test(t)) return equipment.find(e => /spider/i.test(e)) || "";
  if (/photo|reference|r5/.test(t) && has("Canon R5")) return "Canon R5";
  if (/lidar|rtc/.test(t)) return equipment.find(e => /rtc|lidar/i.test(e)) || "";
  return "";
};

/* Lines that are money, not capture work. A catalog id answers it outright;
   only an id-less or custom line is judged on its wording, and that wording
   test is the weak one — atlpow_260909's ONLY capture line was a custom
   "Half-Day On-Location Scan Session — ... Photogrammetry capture + processed
   3D model", the word "Session" threw it out, and the crew sheet went out with
   no capture scope at all. So the word test is allowed to narrow the list but
   never to empty it: a scope listing the session line beats no scope. */
const NON_CAPTURE_CATALOG_IDS = new Set([
  "half_art", "full_art", "partial_mobile", "full_onlocation", "lowpoly_session",
  "truck", "travel_local", "travel_mid", "drone_permit",
]);
// By id alone: certain, and always applied.
const isNonCaptureById = (li) => {
  const id = clean(li?.catalogId).toLowerCase();
  return !!id && id !== "custom" && NON_CAPTURE_CATALOG_IDS.has(id);
};
// Wording that is money whatever the id says. Only "session" is the weak word
// (a custom capture line is often "…Scan Session…"); these three never are.
const isMoneyWording = (li) => /deposit|discount|travel/i.test(clean(li?.description));
// By id, falling back to wording for a line with no id to judge.
const isNonCaptureLine = (li) => {
  const id = clean(li?.catalogId).toLowerCase();
  if (id && id !== "custom") return NON_CAPTURE_CATALOG_IDS.has(id);
  return /session|travel|deposit|discount/i.test(clean(li?.description));
};

/* Quote line notes are written to the client's buyer, so a crew sheet read
   "Volume pricing available, see attached tier sheet". The crew keeps only
   the sentences about the capture. The split uses no lookbehind (Safari
   before 16.4 cannot parse one): a sentence runs to . ! or ? followed by a
   space or the end, so "1.5 m" and "box.com" stay whole. */
// "volume" and "quote" only in their money senses: "the capture volume" and
// "the quoted expression" are crew instructions and must survive.
const MONEY_SENTENCE_RE = /pric|tier sheet|volume (?:pricing|discount|tier)|discount|deposit|invoice|\bquote\b|\$\s?\d/i;
const crewNoteOf = (note) => (clean(note).match(/[^.!?]+(?:[.!?]+(?!\s|$)[^.!?]*)*[.!?]*/g) || [])
  .map(s => s.trim())
  .filter(s => s && !MONEY_SENTENCE_RE.test(s))
  .join(" ");

export function seedScope({ job, quote }) {
  const equipment = list(job?.selectedEquipment).length ? list(job.selectedEquipment) : list(job?.equipment);
  const lines = list(quote?.lineItems).length ? list(quote.lineItems) : list(job?.lineItems);
  const described = lines.filter(li => clean(li?.description));
  const capture = described.filter(li => !isNonCaptureLine(li));
  return (capture.length ? capture : described.filter(li => !isNonCaptureById(li) && !isMoneyWording(li)))
    .slice(0, 6)
    .map((li, i) => ({
      k: itemKey(),
      n: String(i + 1).padStart(2, "0"),
      line: clean(li.description).split(/\s[—–-]\s/)[0],
      note: crewNoteOf(li.notes),
      gear: normalizeGear(guessGear(li.description, equipment)),
    }));
}

/* ── what we're capturing, in the client's words (4.4) ──
   One line per quote line item through a client-safe phrase keyed by catalog
   id: the array is "the camera array", the Leo "the handheld scanner", never
   a rig name. Unknown and custom ids, and every id that is money or a
   session rather than a capture, are left out, never guessed. Every obj_* id
   gives the one object line. Duplicates collapse; the order is the quote's.
   Twin: CAPTURING_PHRASES / seedCapturing in functions crewCard.ts. */
export const CAPTURING_PHRASES = {
  head_scan: "Head, on the camera array.",
  body_scan: "Full body, on the camera array.",
  lowpoly_body: "Full body, on the camera array.",
  leo_body: "Full body, with the handheld scanner.",
  leo_head: "Head, with the handheld scanner.",
  leo_hands: "Hands, with the handheld scanner.",
  leo_torso: "Torso, with the handheld scanner.",
  leo_other: "Body scan, with the handheld scanner.",
  "obj_*": "Each piece, photographed from every angle.", // every obj_* id (the wildcard key)
  env_portalcam: "The space, with a handheld capture camera.",
  env_nimble_onloc: "The space, scanned as it is.",
  env_onloc_full: "The space, scanned as it is.",
};
export const capturingPhraseOf = (catalogId) => {
  const id = clean(catalogId).toLowerCase();
  if (!id) return "";
  if (Object.prototype.hasOwnProperty.call(CAPTURING_PHRASES, id)) return CAPTURING_PHRASES[id];
  return id.startsWith("obj_") ? CAPTURING_PHRASES["obj_*"] : "";
};
export function seedCapturing({ job, quote }) {
  const lines = list(quote?.lineItems).length ? list(quote.lineItems) : list(job?.lineItems);
  const out = [];
  lines.forEach(li => {
    const phrase = capturingPhraseOf(li?.catalogId);
    if (phrase && !out.includes(phrase)) out.push(phrase);
  });
  return out.join("\n");
}

/* ── snapshot (the ONLY thing the public page ever sees) ──
   Allowlist by construction: nothing is spread from the job or the card.
   v2 (2026-09-22): style (outside the fingerprint), subject, crewCall,
   clientContact, extraGear, gear as a list per scope row, and per crew
   member their call, presence and roster phone (on screen only, approved).
   `roster` is an input now: every caller passes it, or its phones are
   missing here and the fingerprint never matches the server's. */
export function buildSnapshot({ job, session, card, contacts, roster = [], otherSessionsToday = [], version = 0, deployedAt = "", expiresAt = "" }) {
  const s = session || {};
  const equipment = list(job?.selectedEquipment).length ? list(job.selectedEquipment) : list(job?.equipment);
  const crewNames = crewNamesOf(job);
  // Ids trimmed on both ends, as the server does (and clientRecipientsOf here).
  const contactsById = new Map(list(contacts).map(c => [clean(c?.id), c]));
  const linked = list(job?.linkedContacts);

  const coordinatorLink = linked.find(l => l?.role === "contact") || linked.find(l => l?.role === "producer");
  const coordinatorContact = coordinatorLink ? contactsById.get(clean(coordinatorLink.contactId)) : null;
  const coordinator = coordinatorContact
    ? [clean(coordinatorContact.name), clean(coordinatorContact.company)].filter(Boolean).join(", ")
    : clean(job?.contactName);

  const vendorGroups = new Map();
  linked.filter(l => l?.role === "vendor").forEach(l => {
    const c = contactsById.get(clean(l.contactId));
    if (!c) return;
    const company = clean(c.company) || "Vendor";
    if (!vendorGroups.has(company)) vendorGroups.set(company, []);
    if (clean(c.name)) vendorGroups.get(company).push(clean(c.name));
  });
  const vendors = [...vendorGroups.entries()].map(([co, names]) => names.length ? `${co} (${names.join(", ")})` : co).join("; ");

  const cityState = [clean(s.locationCity), clean(s.locationState)].filter(Boolean).join(", ");
  const cityStateZip = [cityState, clean(s.locationZip)].filter(Boolean).join(" ");
  const locationLine = s.isStudio
    ? "New York Capture studio, 62 Flushing Ave, Brooklyn"
    : [clean(s.locationVenue), clean(s.locationAddress), cityStateZip].filter(Boolean).join(", ") || clean(s.location);

  // Anonymize: client, production, coordinator, day-of contact and file
  // recipients collapse to the job code (isConfidential: the admin's choice,
  // else on by default for a person client). Free text (prep notes, open
  // items) is NOT scrubbed.
  const confidential = isConfidential({ card, job, contacts });
  const jobCode = clean(job?.jobId);
  // Day-of contact: null = the coordinator; "" = none; else what the admin typed.
  const clientContact = isUnset(card?.clientContact) ? coordinator : clean(card.clientContact);

  return {
    v: 2,
    kind: "crew",
    jobDocId: clean(job?.id),
    jobCode,
    sessionId: clean(s.id) || "root",
    version,
    confidential,
    style: styleOf(card?.style),
    subject: subjectOf(card?.subject),
    date: clean(s.date) || clean(job?.captureDate),
    callTime: clean(s.callTime),
    wrapTime: clean(s.wrapTime),
    crewCall: timeOrEmpty(card?.crewCall),
    transport: clean(job?.transport) || "In Studio",
    location: {
      venue: clean(s.locationVenue), address: clean(s.locationAddress), city: clean(s.locationCity),
      state: clean(s.locationState), zip: clean(s.locationZip), isStudio: !!s.isStudio, line: locationLine,
    },
    clientName: confidential ? (jobCode || "Confidential client") : clean(job?.clientName),
    production: confidential ? "" : clean(job?.project),
    coordinator: confidential ? "" : coordinator,
    clientContact: confidential ? "" : clientContact,
    vendors: confidential ? "" : vendors,
    talentInitials: clean(card?.talentInitials),
    onSiteWithTalent: clean(card?.onSiteWithTalent),
    equipment,
    lenses: list(job?.lenses).map(clean),
    additionalItems: list(job?.additionalItems).map(clean),
    extraGear: linesOf(card?.extraGear),
    prepNotes: clean(job?.prepNotes),
    scope: list(card?.scope).map(x => ({ n: clean(x?.n), line: clean(x?.line), note: clean(x?.note), gear: normalizeGear(x?.gear) })),
    crew: crewNames.map(name => {
      const a = assignmentFor(card, name);
      return {
        name: clean(name), role: clean(a.role), note: clean(a.note),
        call: timeOrEmpty(a.callTime), onSite: presenceOf(card, name), phone: fmtPhone(rosterEntryFor(roster, name)?.phone),
      };
    }),
    checklist: list(card?.checklist).filter(x => x?.enabled !== false).map(x => clean(x?.text)).filter(Boolean),
    openItems: list(card?.openItems).map(x => clean(x?.text)).filter(Boolean),
    deliverable: {
      format: clean(job?.deliverableType),
      turnaround: humanizeTurnaround(job?.turnaround, job?.turnaroundUnit, job?.turnaroundTBD),
      delivery: deliveryOf(job),
    },
    deliverables: [], // slice 3 fills this from the Deliverable Spec
    otherSessionsToday: list(otherSessionsToday).map(x => ({ time: clean(x?.time), label: clean(x?.label) })),
    deployedAt: clean(deployedAt),
    expiresAt: clean(expiresAt),
  };
}

/* A call time as the sheet prints it: "15" is 3:00 PM, "" is "". Twin of
   fmtTime in functions crewCard.ts (utils' fmtTime prints "—" and
   "3:undefined PM" for those two). */
const fmtCallTime = (t) => {
  if (!t) return "";
  const [hh, mm = "00"] = t.split(":");
  const h = parseInt(hh, 10);
  if (isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${suffix}`;
};

/* A call time in minutes after midnight, for ordering ("9:05" before
   "10:30"); none or unreadable sorts last. */
const callMinutes = (t) => {
  const m = /^(\d{1,2})(?::(\d{2}))?/.exec(clean(t));
  return m ? Number(m[1]) * 60 + Number(m[2] || 0) : Infinity;
};
/* Sessions on OTHER jobs the same day (for the day strip). Outside the
   fingerprint, but the preview should show what the deployed sheet shows:
   twin of otherSessionsOn in functions crewCard.ts (job order, each job's
   sessions that day by call time, trimmed dates and ids, the sheet's time
   format). */
export function otherSessionsOn(jobs, dateStr, excludeJobDocId) {
  const out = [];
  const day = clean(dateStr);
  list(jobs).forEach(j => {
    if (clean(j?.id) === clean(excludeJobDocId) || j?.deletedAt) return;
    const sessions = list(j.sessions).length ? list(j.sessions) : (clean(j.captureDate) ? [{ date: j.captureDate, callTime: j.callTime, isStudio: j.isStudio }] : []);
    sessions.filter(s => clean(s?.date) === day)
      .map((s, i) => ({ s, i }))
      .sort((a, b) => (callMinutes(a.s.callTime) - callMinutes(b.s.callTime)) || (a.i - b.i))
      .map(x => x.s)
      .forEach(s => {
        // The job code prefix only, never a client name (item 10): the strip
        // is on every crew sheet, anonymised or not.
        const code = clean(j.jobId).split("_")[0].toUpperCase();
        const where = s.isStudio || j.isStudio ? "studio" : "on-location";
        out.push({ time: clean(s.callTime) ? fmtCallTime(clean(s.callTime)) : "Time TBD", label: code ? `${code} ${where} session` : `Another ${where} session` });
      });
  });
  return out;
}

/* ── fingerprint ──
   Stable JSON of the snapshot minus the per-deploy fields, hashed with two
   FNV-1a passes. Same algorithm on the server. Not a security primitive: it
   only answers "did anything the crew sees change?" */
export function stableStringify(v) {
  if (Array.isArray(v)) return "[" + v.map(stableStringify).join(",") + "]";
  if (v && typeof v === "object") {
    return "{" + Object.keys(v).sort().map(k => JSON.stringify(k) + ":" + stableStringify(v[k])).join(",") + "}";
  }
  return JSON.stringify(v === undefined ? null : v);
}

function fnv1a32(str, seed) {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/* Outside the fingerprint: the per-deploy fields, the day strip, and `style`
   (item 17): choosing a look never flags "changed" and never resends; it
   rides in the snapshot so the deployed page renders it. */
export function fingerprintOf(snapshot) {
  const { version, deployedAt, expiresAt, otherSessionsToday, style, ...rest } = snapshot || {};
  void version; void deployedAt; void expiresAt; void otherSessionsToday; void style;
  const s = stableStringify(rest);
  return fnv1a32(s, 0x811c9dc5).toString(16).padStart(8, "0") + fnv1a32(s, 0x01000193).toString(16).padStart(8, "0");
}

/* Convenience for the dashboard and the section: state of a card for a session. */
export function crewCardState({ job, session, card, contacts, roster }) {
  if (!card?.lastDeploy) return { state: "none", label: "No call sheet", gaps: computeGaps({ job, session, card, roster }).gaps };
  const fp = fingerprintOf(buildSnapshot({ job, session, card, contacts, roster }));
  if (fp !== card.lastDeploy.fingerprint) return { state: "changed", label: `Changed since v${card.lastDeploy.version}`, gaps: computeGaps({ job, session, card, roster }).gaps };
  return { state: "sent", label: `Sent v${card.lastDeploy.version}`, gaps: [] };
}

/* ═══ CLIENT card (twin of the server's client functions) ═══
   Logistics only: where to go, who to reach, what to wear and bring, what
   to expect, deliverable and turnaround. Never money, never crew names
   beyond the on-site contact and the second contact. */
export const blankClientCard = (jobDocId, sessionId) => ({
  id: crewCardId(jobDocId, sessionId),
  jobId: jobDocId,
  sessionId,
  pointPerson: "",
  secondContact: null, // null = never chosen (default rule, secondContactOf); "" = the admin chose None; else a roster name
  subject: null, // null = never seeded; "" = the admin chose "Not sure"; else one of CAPTURE_SUBJECTS
  style: "normcore", // "normcore" | "cool"; outside the fingerprint
  arrival: "", // newline-separated fact lines, all four
  capturing: "",
  bring: "",
  expect: "",
  lengthMinutes: null, // integer minutes 1..1440, or null = today's rule (call to wrap)
  recipientIds: null, // null = default rule (contacts + producers); an array is the explicit tick list
  reviewed: false,
  reviewedFp: "", // fingerprint of the sheet as it stood when Reviewed was ticked; "" = none (see reviewStateOf)
  seedVersion: SEED_VERSION,
  lastDeploy: null,
  recipientStatus: [],
});

/* One wording family for a session's length: "about 45 minutes", "about 1
   hour", "about 1.5 hours". durationLabel reads it from call to wrap;
   durationFromMinutes from the sheet's own lengthMinutes (owner's answer 3),
   which wins when set. Twins in functions crewCard.ts. */
const minutesLabel = (mins) => {
  if (mins < 60) return `about ${mins} minutes`;
  const h = Math.round((mins / 60) * 2) / 2;
  return `about ${h % 1 === 0 ? h : h.toFixed(1)} hour${h === 1 ? "" : "s"}`;
};
export const durationLabel = (callTime, wrapTime) => {
  const m = (t) => { const [h, mi] = String(t || "").split(":").map(Number); return isNaN(h) ? NaN : h * 60 + (mi || 0); };
  const a = m(clean(callTime)); const b = m(clean(wrapTime));
  if (isNaN(a) || isNaN(b) || b <= a) return "";
  return minutesLabel(b - a);
};
export const durationFromMinutes = (mins) => {
  const n = lengthMinutesOf(mins);
  return n ? minutesLabel(n) : "";
};
const inCrew = (job, name) => crewNamesOf(job).some(n => sameName(n, name));
/* The second contact on the Session Sheet. card.secondContact: null/missing =
   never chosen, so the default rule applies: the studio's contact
   (OFFSITE_CONTACT_NAME), always, whether or not he is on this job's crew;
   the snapshot labels him on site when he is (buildClientSnapshot). "" = the
   admin chose None; otherwise a roster name. Never the point person twice.
   Twin: secondContactOf in functions crewCard.ts. */
export const secondContactOf = ({ card }) => {
  const name = typeof card?.secondContact === "string" ? clean(card.secondContact) : OFFSITE_CONTACT_NAME;
  return name && sameName(name, card?.pointPerson) ? "" : name;
};
/* First usable address on a contact: the emails list, then the single field. */
export const firstEmailOf = (c) => (list(c?.emails).map(clean).find(Boolean) || clean(c?.email)).toLowerCase();
/* Who receives the client card. Default (recipientIds null or missing): linked
   contacts with role contact or producer. If the card carries recipientIds, an
   explicit tick list, those linked contacts instead; an empty list means
   nobody, which blocks sending. A person must be a CRM contact linked to the
   job either way: the card never invents an address. */
export function clientRecipientsOf(job, contacts, card) {
  const byId = new Map(list(contacts).map(c => [clean(c?.id), c]));
  const explicit = Array.isArray(card?.recipientIds);
  const picked = explicit ? list(card.recipientIds).map(clean).filter(Boolean) : [];
  const out = [];
  list(job?.linkedContacts).forEach(l => {
    const isDefault = l?.role === "contact" || l?.role === "producer";
    if (explicit ? !picked.includes(clean(l?.contactId)) : !isDefault) return;
    const c = byId.get(clean(l.contactId));
    if (!c) return;
    const email = firstEmailOf(c);
    if (email && !out.some(o => o.email === email)) out.push({ name: clean(c.name), email });
  });
  return out;
}
/* `crewCard` is the call sheet's card for the same session ({} when the
   Suite has not made one): the point person must be on the job's crew and
   marked on site there (presenceOf), and the second contact's label reads
   the same presence. Every caller passes it, or the gaps and the
   fingerprint here differ from the server's. */
export function computeClientGaps({ job, session, card, roster, contacts, crewCard = {} }) {
  const gaps = [];
  const s = session || {};
  const hasLocation = !!(s.isStudio || clean(s.locationAddress) || clean(s.location) || clean(s.locationVenue));
  if (!clean(s.date)) gaps.push({ key: "date", label: "Session date" });
  if (!clean(s.callTime)) gaps.push({ key: "callTime", label: "Call time" });
  if (!hasLocation) gaps.push({ key: "location", label: "Location" });
  const pointName = clean(card?.pointPerson);
  const point = rosterEntryFor(roster, pointName);
  if (!pointName) gaps.push({ key: "pointPerson", label: "On-site point person" });
  else if (!inCrew(job, pointName) || !presenceOf(crewCard, pointName)) gaps.push({ key: "pointPerson", label: "On-site point person (must be crew marked on site)" });
  else if (!clean(point?.phone)) gaps.push({ key: "pointPhone", label: `Roster phone for ${pointName}` });
  const second = secondContactOf({ job, card });
  if (second && !clean(rosterEntryFor(roster, second)?.phone)) gaps.push({ key: "secondPhone", label: `Roster phone for ${second}` });
  if (!clean(card?.arrival)) gaps.push({ key: "arrival", label: "Arrival instructions" });
  if (!clientRecipientsOf(job, contacts, card).length) gaps.push({ key: "recipients", label: "A linked contact with an email" });
  const review = reviewStateOf({ job, session, card, roster, crewCard });
  if (review === "stale") gaps.push({ key: "reviewed", label: "Changed since it was reviewed: tick Reviewed again" });
  else if (review !== "reviewed") gaps.push({ key: "reviewed", label: "Reviewed by an admin" });
  const required = 7 + (second ? 1 : 0);
  return { gaps, required, filled: required - gaps.length };
}
/* "Reviewed" covers the sheet as it stood when the box was ticked. The tick
   stores the fingerprint of that snapshot (card.reviewedFp); any later change
   the client would see (a text edit, a new call time, a new point person)
   moves the fingerprint, and the tick no longer counts: nothing goes out,
   automatically or by hand, until an admin reads it again. A tick made before
   reviewedFp existed carries none and is honoured as it was. The fingerprint
   ignores version and dates, and reviewedFp itself is not in the snapshot.
   Returns "reviewed" | "stale" | "unreviewed". Twin: reviewStateOf in
   functions crewCard.ts. */
export function reviewStateOf({ job, session, card, roster, crewCard = {} }) {
  if (card?.reviewed !== true) return "unreviewed";
  const at = clean(card?.reviewedFp);
  return !at || at === fingerprintOf(buildClientSnapshot({ job, session, card, roster, crewCard })) ? "reviewed" : "stale";
}
/* v2 (2026-09-22): style (outside the fingerprint), capturing, the length
   (duration from lengthMinutes when set, and lengthMinutes itself for the
   calendar file's end), afterwards (slice 3), and second.onSite from the
   call sheet's presence. `crewCard` is the crew card of the same session,
   {} when there is none (presence then follows the role defaults). */
export function buildClientSnapshot({ job, session, card, roster, crewCard = {}, version = 0, deployedAt = "", expiresAt = "" }) {
  const s = session || {};
  const cityState = [clean(s.locationCity), clean(s.locationState)].filter(Boolean).join(", ");
  const cityStateZip = [cityState, clean(s.locationZip)].filter(Boolean).join(" ");
  const isStudio = !!s.isStudio;
  const address = isStudio ? STUDIO_ADDRESS_LINE : ([clean(s.locationVenue), clean(s.locationAddress), cityStateZip].filter(Boolean).join(", ") || clean(s.location));
  const point = rosterEntryFor(roster, clean(card?.pointPerson));
  const secondName = secondContactOf({ job, card });
  // Same shape with or without a second contact, so the fingerprint input is
  // stable. On site = on the job's crew AND present per the call sheet.
  const second = secondName
    ? { name: secondName, phone: fmtPhone(rosterEntryFor(roster, secondName)?.phone), onSite: inCrew(job, secondName) && presenceOf(crewCard, secondName) }
    : { name: "", phone: "", onSite: false };
  const lengthMinutes = lengthMinutesOf(card?.lengthMinutes);
  return {
    v: 2,
    kind: "client",
    jobDocId: clean(job?.id),
    sessionId: clean(s.id) || "root",
    version,
    style: styleOf(card?.style),
    date: clean(s.date) || clean(job?.captureDate),
    callTime: clean(s.callTime),
    wrapTime: clean(s.wrapTime),
    duration: lengthMinutes ? durationFromMinutes(lengthMinutes) : durationLabel(clean(s.callTime), clean(s.wrapTime)),
    lengthMinutes, // 0 when unset: the calendar file then ends at wrap, else its fallback
    location: {
      line: address, isStudio,
      mapsGoogle: isStudio ? STUDIO_MAPS_GOOGLE : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
      mapsApple: `https://maps.apple.com/?q=${encodeURIComponent(address)}`,
    },
    // What is being captured: picks the sheet's labels (what to wear vs what
    // we need on site) and the email's wording. "" = unknown, generic labels.
    subject: subjectOf(card?.subject),
    arrival: clean(card?.arrival),
    capturing: clean(card?.capturing),
    bring: clean(card?.bring),
    expect: clean(card?.expect),
    deliverable: { format: clean(job?.deliverableType), turnaround: humanizeTurnaround(job?.turnaround, job?.turnaroundUnit, job?.turnaroundTBD) },
    afterwards: [], // slice 3 fills this from the Deliverable Spec
    onSite: { name: clean(point?.name), phone: fmtPhone(point?.phone) },
    second,
    studio: { name: "New York Capture", email: "ivin@newyorkcapture.com" },
    deployedAt: clean(deployedAt),
    expiresAt: clean(expiresAt),
  };
}
/* Seeds the client card once; the admin edits and ticks Reviewed.

   Production paperwork: one short fact per line (the sheet keeps the line
   breaks), the talent in the third person, generic equipment words, no method,
   no reassurance. A fact the job does not hold is left out, never guessed: a
   blank field beats a wrong one. Nothing here reads the quote's prose any
   more; the content block and the summary scrape put claims no one had checked
   ("non-invasive", a camera count, "Cleanup and print preparation are not
   included.") onto real sheets.

   Four shapes, from isStudio and the capture subject. A studio session is
   written to someone coming to us; an on-location session to someone whose
   building we are coming to. Every subject-specific line is left EMPTY when
   the subject is unknown; the sheet drops empty sections. */
export function seedClientCard({ job, session, quote, pointPerson, subject }) {
  const s = session || {};
  const isStudio = !!s.isStudio;
  const who = clean(pointPerson);
  const subj = isChosenSubject(subject) ? subject : detectCaptureSubject({ job, quote });
  const equipment = list(job?.selectedEquipment).length ? list(job.selectedEquipment) : list(job?.equipment);
  const isArray = equipment.includes("Head Rig") || equipment.includes("Big Rig");
  const dur = durationLabel(clean(s.callTime), clean(s.wrapTime));
  const durLine = dur ? `${dur.charAt(0).toUpperCase()}${dur.slice(1)} ${isStudio ? "at the studio" : "on site"}.` : "";
  const lines = (...xs) => xs.filter(Boolean).join("\n");

  /* Arrival. Never a placeholder name: with no point person chosen yet the
     line is left out ("your on-site contact will meet you there" shipped to a
     real client). On location the sheet already prints the address above. */
  const arrival = !isStudio
    ? "Someone to meet the crew at the entrance."
    : subj === "people"
      ? lines("Parking on site.", who && `The talent texts or calls ${who} on arrival and is met at the door.`)
      : lines("Parking on site.", who && `On arrival, text or call ${who}. Someone comes to the door.`);

  // Wardrobe for a person is filled per job, from the thread or by hand.
  const bring = subj === "people" ? ""
    : subj === "objects" && isStudio ? lines(
      "The pieces themselves.",
      "At drop-off, tell us about anything fragile, anything that has to sit a particular way, and anything that should not be handled with bare hands.",
    )
    : !isStudio && subj ? lines(
      "Access to the area for the whole window, and enough room to walk around what we are capturing.",
      "We scan the space as it is. Nothing needs to be moved, cleaned up, or staged.",
      "Tell us before the day about anything locked, powered up, or behind glass.",
      subj === "places" && "If the space has lights, having them on helps.",
    )
    : "";

  // A place at the studio has no line of its own yet; it stays empty.
  const expect = subj === "people" && isStudio
    ? lines(isArray && "Each capture takes a fraction of a second.", "A team member directs positioning.", durLine)
    : subj === "people"
      ? lines("The New York Capture crew sets up on site.", "A team member directs positioning.", durLine)
      : subj === "objects" && isStudio
        ? durLine
        : !isStudio && subj
          ? lines("The New York Capture crew on site with the scanning gear, working around your team.", durLine)
          : "";

  // What we're capturing: from the quote's catalog ids alone (seedCapturing).
  return { arrival, capturing: seedCapturing({ job, quote }), bring, expect, subject: subj };
}
export function clientCardState({ job, session, card, roster, contacts, crewCard = {} }) {
  if (!card?.lastDeploy || card.lastDeploy.revokedAt) return { state: "none", label: "No session sheet", gaps: computeClientGaps({ job, session, card, roster, contacts, crewCard }).gaps };
  const fp = fingerprintOf(buildClientSnapshot({ job, session, card, roster, crewCard }));
  if (fp !== card.lastDeploy.fingerprint) return { state: "changed", label: `Changed since v${card.lastDeploy.version}`, gaps: computeClientGaps({ job, session, card, roster, contacts, crewCard }).gaps };
  return { state: "sent", label: `Sent v${card.lastDeploy.version}`, gaps: [] };
}
